
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { canUserCopyPrompt } from "@/services/subscriptionService";
import { copyPromptToClipboard, CopyResult } from "@/utils/promptCopyUtils";
import { toast } from "sonner";
import { hasReachedPromptDailyCopyLimit } from "@/services/promptCopyService";

interface UsePromptCardProps {
  id: string;
  content: string;
  initialLikes?: number;
  initialCopies?: number;
}

const usePromptCard = ({ 
  id, 
  content, 
  initialLikes = 0,
  initialCopies = 0 
}: UsePromptCardProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [isLiked, setIsLiked] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [likeCount, setLikeCount] = useState(initialLikes);
  const [copyCount, setCopyCount] = useState(initialCopies);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [usageInfo, setUsageInfo] = useState<{
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null>(null);

  // Check if prompt is liked by user
  useEffect(() => {
    const checkIfLiked = async () => {
      if (!user) return;
      
      try {
        console.log(`Checking if prompt ${id} is liked by user ${user.id}`);
        const { data, error } = await supabase
          .from('likes')
          .select('id')
          .eq('user_id', user.id)
          .eq('prompt_id', id);
          
        if (!error && data && data.length > 0) {
          setIsLiked(true);
          console.log(`Prompt ${id} is liked by user ${user.id}`);
        }
      } catch (error) {
        console.error('Error checking like status:', error);
      }
    };
    
    checkIfLiked();
  }, [id, user]);
  
  // Check copy usage limits
  useEffect(() => {
    const checkUsageLimits = async () => {
      if (!user) return;
      
      try {
        const usage = await canUserCopyPrompt(user.id);
        setUsageInfo(usage);
        console.log('User copy limits:', usage);
      } catch (err) {
        console.error('Error checking usage limits:', err);
      }
    };
    
    checkUsageLimits();
  }, [user]);

  // Handle toggling like
  const toggleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!user) {
      setShowSignInDialog(true);
      return;
    }
    
    try {
      if (isLiked) {
        console.log(`Unliking prompt ${id}`);
        await supabase
          .from('likes')
          .delete()
          .eq('user_id', user.id)
          .eq('prompt_id', id);
        
        setIsLiked(false);
        setLikeCount(prev => prev - 1);
      } else {
        console.log(`Liking prompt ${id}`);
        await supabase
          .from('likes')
          .insert({ user_id: user.id, prompt_id: id });
        
        setIsLiked(true);
        setLikeCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error updating like:', error);
      toast.error('Could not update like. Please try again.');
    }
  };

  // Handle copying content
  const handleCopyContent = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!user) {
      setShowSignInDialog(true);
      return;
    }
    
    console.log(`Attempting to copy prompt: ${id}`);
    try {
      const result: CopyResult = await copyPromptToClipboard(id, content, user?.id);
      console.log('Copy result:', result);
      
      if (result.copied) {
        setIsCopied(true);
        toast.success("Content copied to clipboard");
        
        // Update the local copy count only if the copy was counted in the total on the server
        if (result.success && result.countedInTotal) {
          console.log('Updating local copy count as this copy was counted in total');
          setCopyCount(prev => prev + 1);
        }
        
        setUsageInfo(result.usageInfo);
        
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
      
      if (result.showUpgradeDialog) {
        setShowUpgradeDialog(true);
      }
    } catch (error) {
      console.error('Error copying content:', error);
      toast.error('Could not copy content. Please try again.');
    }
  };

  // Handle sign in dialog interactions
  const handleSignInClose = () => {
    setShowSignInDialog(false);
  };
  
  const handleNavigateToSignIn = () => {
    setShowSignInDialog(false);
    navigate('/sign-in');
  };
  
  const handlePromptClick = (e: React.MouseEvent) => {
    navigate(`/prompts/${id}`);
  };

  return {
    isLiked,
    likeCount,
    copyCount,
    isCopied,
    showUpgradeDialog,
    setShowUpgradeDialog,
    showSignInDialog,
    setShowSignInDialog,
    handleSignInClose,
    handleNavigateToSignIn,
    usageInfo,
    toggleLike,
    handleCopyContent,
    handlePromptClick
  };
};

export default usePromptCard;
